<template>
  <div
    class="outer-box"
    :data-testid="`content-creator-${part}-carousel`"
    :style="cssProps"
  >
    <v-btn
      v-if="showControls"
      icon
      elevation="2"
      size="small"
      class="arrow arrow-left"
      :data-testid="`content-creator-${part}-carousel-prev`"
      @click="goToSlidePrev"
    >
      <v-icon> mdi-chevron-left </v-icon>
    </v-btn>
    <v-btn
      v-if="showControls"
      icon
      elevation="2"
      size="small"
      class="arrow arrow-right"
      :data-testid="`content-creator-${part}-carousel-next`"
      @click="goToSlideNext"
    >
      <v-icon> mdi-chevron-right </v-icon>
    </v-btn>

    <Carousel3d
      :ref="`carousel-${part}`"
      :width="dimensions.width"
      :height="dimensions.height"
      class="carousel"
      :perspective="0"
      :space="carouselSpace"
      :border="0"
      :startIndex="startIndex"
    >
      <Slide
        v-for="(choice, i) in choices"
        :key="i"
        :index="i"
        class="main-box"
      >
        <template v-slot="{ index, isCurrent, leftIndex, rightIndex }">
          <component
            :is="getComponent(part, choice)"
            :data-index="index"
            :style="currentSliderStyles(isCurrent)"
            :current-width="dimensions.width"
            :recalculate-height="recalculateHeight"
            :class="{
              onLeft: leftIndex >= 0,
              onRight: rightIndex >= 0,
            }"
            :content="choice"
            :is-markdown="shouldParseMarkdown"
            :is-loading="isLoading"
            :is-current="isCurrent"
            :selected-image-source-type="selectedImageSourceType"
            :selected-image-source-variant="selectedImageSourceVariant"
            :topic-id="topicId"
            :is-stock-photos-ready="isStockPhotosReady"
            @handleClick="handleClick(index, isCurrent, getComponent(part, choice))"
            @currentHeight="calculateCurrentHeight($event, isCurrent)"
            @image-updated="handleImageUpdate"
            @change="handleContentChange"
          />
        </template>
      </Slide>
    </Carousel3d>
  </div>
</template>

<script>
import { computed } from 'vue'

import { Carousel3d, Slide } from '@/components/carousel'
import { breakpointXsAndDown } from '@/lib/mobile'
import EditContent from '@/modules/contentCreator/creator/EditContent.vue'

import ImageBox from './Box/ImageBox.vue'
import TextBox from './Box/TextBox.vue'
import VideoBox from './Box/VideoBox.vue'
import KindType from './enums/KindType'

export default {
  components: {
    EditContent,
    Carousel3d,
    Slide,
    TextBox,
    ImageBox,
    VideoBox,
  },
  props: {
    kind: {
      type: String,
      default: '',
    },
    part: {
      type: String,
      default: '',
    },
    startIndex: {
      type: Number,
      default: 0,
    },
    choices: {
      type: Array,
      default: () => [],
    },
    hasAtLeastOneImage: {
      type: Boolean,
      default: false,
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
    selectedImageSourceType: {
      type: String,
      default: '',
    },
    selectedImageSourceVariant: {
      type: String,
      default: '',
    },
    topicId: {
      type: String,
      required: true,
    },
    isStockPhotosReady: {
      type: Boolean,
      default: false,
    },
  },
  provide() {
    return {
      textKind: computed(() => this.kind),
    }
  },
  data() {
    return {
      currentHeight: null,
      contentForEditing: null,
      recalculateHeight: null,
    }
  },
  computed: {
    hasTextAboveImage() {
      return this.isStructureTypeTextAboveImage && this.hasTextForKind && this.hasAtLeastOneImage
    },
    hasImageBeyondText() {
      return this.isStructureTypeTextAboveImage && this.hasImageForKind
    },
    hasImageAboveText() {
      return this.isStructureTypeImageAboveText && this.hasImageForKind
    },
    hasTextBeyondImage() {
      return this.isStructureTypeImageAboveText && this.hasTextForKind && this.hasAtLeastOneImage
    },
    isStructureTypeTextAboveImage() {
      const types = [
        KindType.facebookPost,
        KindType.linkedinPost,
        KindType.facebookAds,
        KindType.gmbNewsPost,
        KindType.gmbProductDescription,
      ]

      return types.includes(this.kind)
    },
    isStructureTypeImageAboveText() {
      const types = [KindType.instagramPost, KindType.websiteText, KindType.whatsappStatus]
      return types.includes(this.kind)
    },
    showControls() {
      return this.choices.length > 1
    },
    cssProps() {
      const divider = this.breakpointXsAndDown() ? 2.4 : 2

      return {
        '--primary-color': this.$vuetify.theme.global.current.colors.primary,
        '--slide-width-negative': `-${this.dimensions.width / divider}px`,
        '--slide-width': `${this.dimensions.width / divider}px`,
      }
    },
    hasImageForKind() {
      return this.part === 'image' && this.choices?.length > 0
    },
    hasTextForKind() {
      return this.part === 'text' && this.choices?.length > 0
    },
    carouselSpace() {
      return KindType[this.kind] === KindType.websiteText ||
        KindType[this.kind] === KindType.customerMailing ||
        KindType[this.kind] === KindType.interview ||
        KindType[this.kind] === KindType.blogPost
        ? 680
        : 490
    },
    dimensions() {
      const isPortraitTextKind = KindType[this.kind] === KindType.whatsappStatus
      const whatsappImageWidth = 390
      const whatsappImageHeight = 688
      const textDimensions = {
        width: this.breakpointXsAndDown()
          ? window.innerWidth - 20
          : isPortraitTextKind
            ? whatsappImageWidth
            : 400,
        height: this.currentHeight + (isPortraitTextKind ? 60 : 70),
      }

      const imageDimensions = this.hasImageForKind
        ? {
            width: this.breakpointXsAndDown()
              ? window.innerWidth - 20
              : isPortraitTextKind
                ? whatsappImageWidth
                : 400,
            height: isPortraitTextKind
              ? this.breakpointXsAndDown()
                ? Math.round((window.innerWidth - 20) * (16 / 9))
                : whatsappImageHeight
              : 350,
          }
        : { width: 0, height: 0 }

      if (
        KindType[this.kind] === KindType.websiteText ||
        KindType[this.kind] === KindType.customerMailing ||
        KindType[this.kind] === KindType.newsletter ||
        KindType[this.kind] === KindType.interview ||
        KindType[this.kind] === KindType.blogPost
      ) {
        textDimensions.width = this.breakpointXsAndDown() ? window.innerWidth - 20 : 800
        if (this.hasImageForKind) {
          imageDimensions.width = this.breakpointXsAndDown() ? window.innerWidth - 20 : 800
        }
      }

      return {
        image: imageDimensions,
        text: textDimensions,
      }[this.part]
    },
    shouldParseMarkdown() {
      const markdownTypes = [
        KindType.facebookAds,
        KindType.websiteText,
        KindType.interview,
        KindType.blogPost,
      ]
      return this.part === 'text' && markdownTypes.includes(this.kind)
    },
  },

  methods: {
    calculateCurrentHeight(height, isCurrent) {
      if (isCurrent) {
        this.currentHeight = height
      }
    },
    currentSliderStyles(isCurrent) {
      if (!isCurrent) {
        return {
          border: '2px solid ' + this.$vuetify.theme.global.current.colors.primary,
        }
      }

      const styles = {
        border: '5px solid ' + this.$vuetify.theme.global.current.colors.primary,
      }
      if (this.hasTextAboveImage || this.hasImageAboveText) {
        styles['border-bottom'] = 'none'
        styles['border-top'] = '5px solid ' + this.$vuetify.theme.global.current.colors.primary
      }

      if (this.hasTextBeyondImage || this.hasImageBeyondText) {
        styles['border-top'] = 'none'
        styles['border-bottom'] = '5px solid ' + this.$vuetify.theme.global.current.colors.primary
      }

      return styles
    },
    breakpointXsAndDown,
    goToSlideNext() {
      this.recalculateHeight = this.$refs[`carousel-${this.part}`].currentIndex
      this.$refs[`carousel-${this.part}`].goNext()
      this.$emit('change', {
        part: this.part,
        content: this.choices[this.$refs[`carousel-${this.part}`].currentIndex],
      })
    },
    goToSlidePrev() {
      this.recalculateHeight = this.$refs[`carousel-${this.part}`].currentIndex
      this.$refs[`carousel-${this.part}`].goPrev()
      this.$emit('change', {
        part: this.part,
        content: this.choices[this.$refs[`carousel-${this.part}`].currentIndex],
      })
    },
    getComponent(part, content) {
      // For image part, check if it's a video
      if (part === 'image' && content?.isVideo) {
        return 'VideoBox'
      }

      return {
        text: 'TextBox',
        title: 'TextBox',
        image: 'ImageBox',
      }[part]
    },
    handleClick(index, isCurrent, part) {
      if (isCurrent && part === 'TextBox') {
        this.$emit('openEditTextDialog', index)
        return
      }
      if (isCurrent && part === 'VideoBox') {
        this.$emit('openVideoPreview', this.choices[index])
        return
      }
      this.$emit('change', { part: this.part, content: this.choices[index] })
    },
    handleImageUpdate(imageData) {
      this.$emit('image-updated', imageData)
    },
    handleContentChange(updatedContent) {
      const index = this.choices.findIndex((choice) => choice.id === updatedContent.id)
      if (index !== -1) {
        const updatedChoices = [
          ...this.choices.slice(0, index),
          updatedContent,
          ...this.choices.slice(index + 1),
        ]

        this.$emit('change', { part: this.part, content: updatedContent, choices: updatedChoices })
      }
    },
  },
  emits: ['change', 'openEditTextDialog', 'image-updated', 'openVideoPreview'],
}
</script>

<style scoped>
.carousel {
  margin: -10px auto !important;
}

.main-box {
  border-radius: 5px;
  background-color: white;
  z-index: 4;
}

.outer-box {
  position: relative;
}

.arrow {
  position: absolute;
  color: var(--primary-color);
  cursor: pointer;
  z-index: 2;
  background-color: white;
  border-radius: 50px;
  border-color: var(--primary-color);
  border-width: 2px;
  border-style: solid;
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
  margin: auto;
}
.arrow-left {
  transform: translateX(var(--slide-width-negative));
}

.arrow-right {
  transform: translateX(var(--slide-width));
}

.arrow-left,
.arrow-right {
  width: 40px;
}

@media (max-width: 600px) {
  .arrow-left,
  .arrow-right {
    border: none;
    width: 26px;
    height: 80px !important;
    border-radius: 3px !important;
  }

  .arrow-left {
    transform: translateX(calc(var(--slide-width-negative) - 20px));
  }
  .arrow-right {
    transform: translateX(calc(var(--slide-width) + 20px));
  }
}
</style>
